/**
 * 客户管理页面 — 销售角色专用
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Loader2, X } from 'lucide-react';
import { api, getErrorMessage } from '@/services/api';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import styles from './Users.module.css';

interface Customer {
  user_id: number; user_name: string; account: string; company: string;
  position: string; status: number; create_time: string;
}

export default function CustomerManagement() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ user_name:'', account:'', company:'', position:'' });

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.request<any[]>('/promotions/customers');
      setCustomers((res.data||[]).map((c:any)=>({user_id:c.user_id,user_name:c.user_name,account:c.account,company:c.company||'',position:c.position||'',status:c.status,create_time:c.create_time||''})));
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const handleCreate = () => { setEditing(null); setForm({user_name:'',account:'',company:'',position:''}); setShowModal(true); };
  const handleEdit = (c: Customer) => { setEditing(c); setForm({user_name:c.user_name,account:c.account,company:c.company||'',position:c.position||''}); setShowModal(true); };
  const handleSubmit = async (e: React.FormEvent) => { e.preventDefault();
    if (!form.user_name.trim() || !form.account.trim()) { showToast('warning','请填写完整'); return; }
    setSaving(true);
    try {
      if (editing) await api.request(`/promotions/customers/${editing.user_id}`, { method:'PUT', body:JSON.stringify({user_name:form.user_name,company:form.company,position:form.position}) });
      else await api.request('/promotions/customers', { method:'POST', body:JSON.stringify({...form, password:'123456', max_view_level:1}) });
      showToast('success', editing?'修改成功':'创建成功'); setShowModal(false); loadCustomers();
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c: Customer) => {
    const ok = await confirm({ title:'删除客户', message:`确定删除客户「${c.user_name}」吗？\n\n删除后将同时清除该客户的登录账号和所有观看记录，此操作不可恢复。`, confirmText:'确认删除', danger:true });
    if (!ok) return;
    try { await api.request(`/promotions/customers/${c.user_id}`, { method:'DELETE' }); showToast('success','已删除'); loadCustomers(); }
    catch (err) { showToast('error', getErrorMessage(err)); }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d==='asc'?'desc':'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const SortTh = ({ field, children, cls }: { field: string; children: React.ReactNode; cls?: string }) => (
    <th className={cls || styles.tableHeaderCell} style={{cursor:'pointer',userSelect:'none'}} onClick={() => handleSort(field)}>
      {children} {sortField===field ? (sortDir==='asc'?'▲':'▼') : ''}
    </th>
  );

  let filtered = customers.filter(c => {
    if (searchQuery && !c.user_name.includes(searchQuery) && !c.account.includes(searchQuery) && !(c.company||'').includes(searchQuery)) return false;
    if (filterStartDate && c.create_time && c.create_time.slice(0,10) < filterStartDate) return false;
    if (filterEndDate && c.create_time && c.create_time.slice(0,10) > filterEndDate) return false;
    return true;
  });

  if (sortField) {
    filtered.sort((a: any, b: any) => {
      let va = a[sortField] ?? '', vb = b[sortField] ?? '';
      const cmp = typeof va==='number' ? va-vb : String(va).localeCompare(String(vb));
      return sortDir==='asc' ? cmp : -cmp;
    });
  }

  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('zh-CN') : '-';

  return (
    <div className={styles.container}>
      <div className={styles.header}><h1 className={styles.title}>客户管理</h1></div>
      <div className={styles.searchBar}>
        <div className={styles.searchInput}>
          <Search size={18} className={styles.searchIcon} strokeWidth={1.5} />
          <input type="text" placeholder="搜索名称、账号或公司..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} />
          {searchQuery && <button className={styles.clearSearch} onClick={()=>setSearchQuery('')}><X size={16}/></button>}
        </div>
        <div className={styles.filters}>
          <input type="date" value={filterStartDate} onChange={e=>setFilterStartDate(e.target.value)} className={styles.dateInput} />
          <span className={styles.dateSep}>—</span>
          <input type="date" value={filterEndDate} onChange={e=>setFilterEndDate(e.target.value)} className={styles.dateInput} />
          <button
            className={styles.clearFilter}
            onClick={()=>{setSearchQuery('');setFilterStartDate('');setFilterEndDate('');}}
            style={{opacity: (searchQuery||filterStartDate||filterEndDate) ? 1 : 0.4, cursor: (searchQuery||filterStartDate||filterEndDate) ? 'pointer' : 'default'}}
          >清除筛选</button>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead><tr>
            <SortTh field="user_name" cls={styles.tableHeaderCell}>名称</SortTh>
            <SortTh field="account" cls={styles.tableHeaderCell}>账号</SortTh>
            <SortTh field="company" cls={styles.tableHeaderCell}>客户公司</SortTh>
            <SortTh field="position" cls={styles.tableHeaderCell}>客户职位</SortTh>
            <SortTh field="create_time" cls={`${styles.tableHeaderCell} ${styles.centerCell}`}>创建时间</SortTh>
            <th className={`${styles.tableHeaderCell} ${styles.centerCell}`}>操作</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={{textAlign:'center',padding:40}}><Loader2 size={20}/></td></tr> :
            filtered.map(c => (
              <motion.tr key={c.user_id} initial={{opacity:0}} animate={{opacity:1}}>
                <td className={styles.tableHeaderCell}><span style={{fontWeight:500}}>{c.user_name}</span></td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{c.account}</td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{c.company || '-'}</td>
                <td className={styles.tableHeaderCell} style={{color:'var(--ink-500)',fontSize:'.8125rem'}}>{c.position || '-'}</td>
                <td className={`${styles.tableHeaderCell} ${styles.centerCell}`} style={{fontSize:'.8125rem',color:'var(--ink-500)'}}>{formatDate(c.create_time)}</td>
                <td className={`${styles.tableHeaderCell} ${styles.centerCell}`}>
                  <div className={styles.tableActions}>
                    <button className={styles.tableActionBtn} onClick={()=>handleEdit(c)}><Edit size={14}/></button>
                    <button className={styles.tableActionBtn} onClick={()=>handleDelete(c)}><Trash2 size={14}/></button>
                  </div>
                </td>
              </motion.tr>
            ))}
            {!loading && filtered.length===0 && <tr><td colSpan={6} style={{textAlign:'center',padding:40,color:'#9ca3af'}}>暂无客户</td></tr>}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div className={styles.modalOverlay} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowModal(false)}>
            <motion.div className={styles.modal} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
              <div className={styles.modalHeader}><h2 className={styles.modalTitle}>{editing?'编辑客户':'新增客户'}</h2>
                <button className={styles.modalClose} onClick={()=>setShowModal(false)}><X size={20}/></button></div>
              <form onSubmit={handleSubmit} className={styles.modalBody}>
                <div className={styles.formGroup}><label>名称 *</label><input value={form.user_name} onChange={e=>setForm({...form,user_name:e.target.value})} required/></div>
                {!editing && <div className={styles.formGroup}><label>账号 *</label><input value={form.account} onChange={e=>setForm({...form,account:e.target.value})} required/></div>}
                <div className={styles.formGroup}><label>客户公司</label><input value={form.company} onChange={e=>setForm({...form,company:e.target.value})} placeholder="客户所属公司"/></div>
                <div className={styles.formGroup}><label>客户职位</label><input value={form.position} onChange={e=>setForm({...form,position:e.target.value})} placeholder="如：采购经理"/></div>
                <div className={styles.modalFooter}>
                  <button type="button" onClick={()=>setShowModal(false)} className={styles.cancelButton}>取消</button>
                  <button type="submit" disabled={saving} className={styles.submitButton}>{saving?<Loader2 size={14}/>:'保存'}</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button className={styles.fab} onClick={handleCreate} whileHover={{scale:1.1}} whileTap={{scale:.9}}>
        <Plus size={28} strokeWidth={2}/>
      </motion.button>
    </div>
  );
}
